<script setup lang="ts">
import { ref, watch } from 'vue'
import { site } from '@/i18n'

const props = defineProps<{ active: boolean }>()

const KEY = 'u2_compliance_v1'
// 模块级: 拒绝后即使组件重挂载也保持整站封锁
const blocked = ref(false)
const show = ref(false)

function hasAgreed() {
  try { return localStorage.getItem(KEY) === '1' } catch { return false }
}

// 进入首页且未同意过才弹
watch(
  () => props.active,
  (v) => { if (v && !blocked.value && !hasAgreed()) show.value = true },
  { immediate: true }
)

function agree() {
  try { localStorage.setItem(KEY, '1') } catch { /* 隐私模式忽略 */ }
  show.value = false
}

function decline() {
  show.value = false
  blocked.value = true
  // 尝试关闭窗口; 多数浏览器禁止脚本关闭普通标签页, 兜底由全屏遮罩阻断访问
  try { window.open('about:blank', '_self'); window.close() } catch { /* noop */ }
}
</script>

<template>
  <!-- 合规与反诈骗提示 -->
  <Transition name="cmp">
    <div v-if="show" class="cmp-mask" role="dialog" aria-modal="true" :aria-label="site.compliance.title">
      <div class="cmp-card">
        <div class="cmp-badge">!</div>
        <h2 class="cmp-title">{{ site.compliance.title }}</h2>
        <p class="cmp-body">{{ site.compliance.body }}</p>
        <div class="cmp-actions">
          <button class="cmp-btn cmp-decline" @click="decline">{{ site.compliance.decline }}</button>
          <button class="cmp-btn cmp-agree" @click="agree">{{ site.compliance.agree }}</button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- 拒绝后的整站封锁页 -->
  <div v-if="blocked" class="cmp-mask cmp-block">
    <div class="cmp-card">
      <h2 class="cmp-title">{{ site.compliance.blockedTitle }}</h2>
      <p class="cmp-body">{{ site.compliance.blockedBody }}</p>
    </div>
  </div>
</template>

<style scoped>
.cmp-mask {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(4, 10, 24, 0.62);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.cmp-card {
  width: 100%;
  max-width: 460px;
  padding: 32px 28px 26px;
  text-align: center;
  background: var(--u2-elevated, #fff);
  border: 1px solid var(--u2-stroke, rgba(0, 0, 0, 0.08));
  border-radius: var(--radius-lg, 18px);
  box-shadow: var(--shadow-lg, 0 24px 60px rgba(0, 0, 0, 0.28));
}
.cmp-badge {
  width: 46px;
  height: 46px;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
  color: #fff;
  background: var(--u2-warning, #f5a623);
  border-radius: 50%;
}
.cmp-title {
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 700;
  color: var(--u2-emphasis, #10131a);
  font-family: var(--font-display, inherit);
}
.cmp-body {
  margin: 0 0 24px;
  font-size: 15px;
  line-height: 1.7;
  color: var(--u2-text, #3a4150);
}
.cmp-actions {
  display: flex;
  gap: 12px;
}
.cmp-btn {
  flex: 1;
  padding: 11px 18px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border-radius: var(--radius-md, 12px);
  transition: filter 0.15s ease, background 0.15s ease;
}
.cmp-agree {
  color: var(--u2-primary-text, #fff);
  background: var(--u2-primary, #2f6bff);
  border: 1px solid transparent;
}
.cmp-agree:hover { filter: brightness(1.06); }
.cmp-decline {
  color: var(--u2-muted, #6b7280);
  background: transparent;
  border: 1px solid var(--u2-stroke-strong, rgba(0, 0, 0, 0.16));
}
.cmp-decline:hover { background: var(--u2-hover, rgba(0, 0, 0, 0.04)); }

/* 封锁页禁交互穿透 */
.cmp-block { cursor: not-allowed; }

.cmp-enter-active,
.cmp-leave-active { transition: opacity 0.22s ease; }
.cmp-enter-from,
.cmp-leave-to { opacity: 0; }

@media (max-width: 480px) {
  .cmp-card { padding: 26px 20px 22px; }
  .cmp-actions { flex-direction: column-reverse; }
}
</style>
